import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  IconButton,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  OutlinedInput,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { tr } from 'date-fns/locale';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import { useTranslation } from 'react-i18next';
import { useReportManagement } from '../../../hooks/useReportManagement';
import { useAuth, useReportTypes, useJobTypes } from '../../../hooks';
import { formatDateForHasura } from '../../../utils/dateUtils';

const COLUMN_COLORS = [
  '#ef5350', '#ff9800', '#fdd835', '#4caf50', '#2196f3',
  '#9c27b0', '#00bcd4', '#795548', '#607d8b', '#bdbdbd',
];

function ColorPicker({ value, onChange }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
      {COLUMN_COLORS.map((color) => (
        <Tooltip title={color} key={color}>
          <Box
            onClick={() => onChange(color)}
            sx={{
              width: 22, height: 22, borderRadius: '50%', bgcolor: color, cursor: 'pointer',
              border: value === color ? '2.5px solid' : '2px solid transparent',
              borderColor: value === color ? 'primary.main' : 'transparent',
              outline: value === color ? '2px solid white' : 'none',
              outlineOffset: '-3px', transition: 'transform 0.1s',
              '&:hover': { transform: 'scale(1.2)' },
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
}

const CreateFlowDialog = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { createFlow, createColumn, populateFlowItems } = useReportManagement();
  const { user } = useAuth();
  const { reportTypes } = useReportTypes();
  const { jobTypes: allJobTypes } = useJobTypes();

  const P = 'reportManagement.createFlow';

  // Tüm rapor türlerindeki etiketleri unique olarak topla
  const allTagOptions = React.useMemo(() => {
    const tagSet = new Set();
    (reportTypes || []).forEach((rt) => {
      const tags = rt.report_type_tags || rt.tags || [];
      tags.forEach((tag) => { if (tag) tagSet.add(tag); });
    });
    return [...tagSet].sort().map((tag) => ({ value: tag, label: tag }));
  }, [reportTypes]);

  // Is turleri sirkete ozel ve dinamik (public.job_types) — burada sabit bir
  // enum listesi duruyordu ve iki sorun uretiyordu:
  //   1. Sirketin kendi actigi turler (TEKMER, DIGER...) hic secilemiyordu.
  //   2. Saklanan deger enum metniydi; eslesme de metin uzerinden yapildigi
  //      icin ayni turun farkli yazimlari (ornegin dinamik ad 'AR_GE Merkezi'
  //      iken akista 'AR_GE') isleri sessizce eliyordu.
  // Artik Isler ekraninin kullandigi kaynagin aynisindan besleniyor ve
  // job_types.id sakliyoruz; iki ekran ayni sonucu veriyor.
  const JOB_TYPE_OPTIONS = useMemo(() => {
    const rows = Array.isArray(allJobTypes) ? allJobTypes : [];
    return rows
      .filter((jt) => jt.status === 'active')
      .filter((jt) => (user?.companyId ? jt.companyId === user.companyId : true))
      .map((jt) => ({ value: jt.id, label: jt.name }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label), 'tr'));
  }, [allJobTypes, user?.companyId]);

  // JOB_LIST mode: optionally narrow by job_status. Empty = all (legacy).
  // REPORT_PLAN flows ignore this filter (reports don't have job_status).
  // public.job_status tablosundaki 15 degerin TAMAMI burada olmali. Asama
  // durumlari (1./2. Asama Yazimda/Degerlendirmede/Onaylandi) eksikti; bu
  // yuzden o durumdaki isler ne "Onaylandi" ne "Kapatildi" filtresine
  // giriyordu ve iki filtrenin toplami is listesi sayisini tutmuyordu.
  const JOB_STATUS_OPTIONS = [
    { value: 'YAZIMDA',                   label: t('jobs.jobStatuses.YAZIMDA',                   { defaultValue: 'Yazımda' }) },
    { value: 'DEGERLENDIRMEDE',           label: t('jobs.jobStatuses.DEGERLENDIRMEDE',           { defaultValue: 'Değerlendirmede' }) },
    { value: 'ONAYLANDI',                 label: t('jobs.jobStatuses.ONAYLANDI',                 { defaultValue: 'Onaylandı' }) },
    { value: 'ASAMA_BIR_YAZIMDA',         label: t('jobs.jobStatuses.ASAMA_BIR_YAZIMDA',         { defaultValue: '1. Aşama Yazımda' }) },
    { value: 'ASAMA_BIR_DEGERLENDIRMEDE', label: t('jobs.jobStatuses.ASAMA_BIR_DEGERLENDIRMEDE', { defaultValue: '1. Aşama Değerlendirmede' }) },
    { value: 'ASAMA_BIR_ONAYLANDI',       label: t('jobs.jobStatuses.ASAMA_BIR_ONAYLANDI',       { defaultValue: '1. Aşama Onaylandı' }) },
    { value: 'ASAMA_IKI_YAZIMDA',         label: t('jobs.jobStatuses.ASAMA_IKI_YAZIMDA',         { defaultValue: '2. Aşama Yazımda' }) },
    { value: 'ASAMA_IKI_DEGERLENDIRMEDE', label: t('jobs.jobStatuses.ASAMA_IKI_DEGERLENDIRMEDE', { defaultValue: '2. Aşama Değerlendirmede' }) },
    { value: 'ASAMA_IKI_ONAYLANDI',       label: t('jobs.jobStatuses.ASAMA_IKI_ONAYLANDI',       { defaultValue: '2. Aşama Onaylandı' }) },
    { value: 'REDDEDILDI',                label: t('jobs.jobStatuses.REDDEDILDI',                { defaultValue: 'Reddedildi' }) },
    { value: 'KAPATILDI',                 label: t('jobs.jobStatuses.KAPATILDI',                 { defaultValue: 'Kapatıldı' }) },
    { value: 'TAMAMLANDI',                label: t('jobs.jobStatuses.TAMAMLANDI',                { defaultValue: 'Tamamlandı' }) },
    { value: 'DEVRALINDI',                label: t('jobs.jobStatuses.DEVRALINDI',                { defaultValue: 'Devralındı' }) },
    { value: 'ORTAK_BULUNDU',             label: t('jobs.jobStatuses.ORTAK_BULUNDU',             { defaultValue: 'Ortak Bulundu' }) },
    { value: 'DANISMANLIK_SONLANDIRILDI', label: t('jobs.jobStatuses.DANISMANLIK_SONLANDIRILDI', { defaultValue: 'Danışmanlık Sonlandırıldı' }) },
  ];

  const defaultColumns = (t(`${P}.defaultColumns`, { returnObjects: true }) || []).map((title, idx) => {
    const colors = ['#ff9800', '#2196f3', '#9c27b0', '#4caf50'];
    // Final column is the "Completed" stage by default — wire its automatic
    // action so dragging a card there marks the underlying report COMPLETED.
    const isCompletion = idx === 3;
    return {
      column_title: title,
      column_color: colors[idx] || '#bdbdbd',
      is_completion_column: isCompletion,
      column_action: isCompletion ? 'SET_REPORT_COMPLETED' : null,
    };
  });

  const [flowTitle, setFlowTitle] = useState('');
  const [description, setDescription] = useState('');
  const [jobTypes, setJobTypes] = useState([]);
  const [sourceType, setSourceType] = useState('JOB_LIST');
  const [filterTags, setFilterTags] = useState([]);
  // New Wave 2 fields
  const [flowType, setFlowType] = useState('CONTINUOUS');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [reportTypeIds, setReportTypeIds] = useState([]);
  const [jobStatuses, setJobStatuses] = useState([]);
  const [columns, setColumns] = useState(
    defaultColumns.map((col, idx) => ({ ...col, _key: Date.now() + idx }))
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (open) {
      setFlowTitle('');
      setDescription('');
      setJobTypes([]);
      setSourceType('JOB_LIST');
      setFilterTags([]);
      setFlowType('CONTINUOUS');
      setStartDate(null);
      setEndDate(null);
      setReportTypeIds([]);
      setJobStatuses([]);
      setColumns(defaultColumns.map((col, idx) => ({ ...col, _key: Date.now() + idx })));
      setError('');
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const addColumn = () => {
    setColumns((prev) => [...prev, { _key: Date.now(), column_title: '', column_color: '#bdbdbd', is_completion_column: false, column_action: null }]);
  };

  const removeColumn = (key) => setColumns((prev) => prev.filter((c) => c._key !== key));

  const updateCol = (key, field, value) => {
    setColumns((prev) => prev.map((c) => (c._key === key ? { ...c, [field]: value } : c)));
  };

  // Single source of truth for action changes — keeps the legacy
  // is_completion_column boolean in sync so the existing green
  // progress-bar UI continues to work without any extra wiring.
  const updateColAction = (key, action) => {
    setColumns((prev) => prev.map((c) => (
      c._key === key
        ? {
            ...c,
            column_action: action || null,
            is_completion_column: action === 'SET_REPORT_COMPLETED',
          }
        : c
    )));
  };

  const moveColumn = (index, dir) => {
    const arr = [...columns];
    const target = index + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    setColumns(arr);
  };

  const handleSubmit = async () => {
    if (!flowTitle.trim()) { setError(t(`${P}.flowTitleRequired`)); return; }
    if (columns.length === 0) { setError(t(`${P}.atLeastOneColumn`)); return; }
    if (columns.find((c) => !c.column_title.trim())) { setError(t(`${P}.columnTitleRequired`)); return; }

    // Date validation for new flow_type field
    if (!startDate) { setError(t(`${P}.startDateRequired`)); return; }
    if (flowType === 'PERIOD_BASED') {
      if (!endDate) { setError(t(`${P}.endDateRequired`)); return; }
      if (endDate <= startDate) { setError(t(`${P}.endAfterStart`)); return; }
    }

    setError('');
    setLoading(true);

    try {
      let filterConfig = {};
      if (sourceType === 'REPORT_PLAN') {
        filterConfig = {
          tags: filterTags.length > 0 ? filterTags : [],
        };
      }

      const newFlow = await createFlow({
        flow_title: flowTitle.trim(),
        flow_description: description.trim() || null,
        job_types: jobTypes,
        source_type: sourceType,
        filter_config: filterConfig,
        flow_type: flowType,
        start_date: formatDateForHasura(startDate),
        end_date: flowType === 'PERIOD_BASED' ? formatDateForHasura(endDate) : null,
        report_type_ids: reportTypeIds,
        job_statuses: sourceType === 'JOB_LIST' ? jobStatuses : [],
        is_active: true,
        company_id: user?.companyId,
      });

      if (!newFlow?.id) throw new Error(t(`${P}.flowCreateError`));

      // Create columns first; capture the first column's id for auto-population.
      let firstColumnId = null;
      for (let i = 0; i < columns.length; i++) {
        const col = columns[i];
        const created = await createColumn(newFlow.id, {
          column_title: col.column_title.trim(),
          column_order: i,
          column_color: col.column_color,
          is_completion_column: col.is_completion_column,
          column_action: col.column_action || null,
        });
        if (i === 0) firstColumnId = created?.id || null;
      }

      // Auto-populate items into the first column. Best-effort; failure
      // shouldn't block flow creation.
      if (firstColumnId) {
        try {
          await populateFlowItems(newFlow, firstColumnId);
        } catch (popErr) {
          // eslint-disable-next-line no-console
          console.error('Auto-populate flow items failed:', popErr);
        }
      }

      if (onSuccess) onSuccess(newFlow);
    } catch (err) {
      setError(err.message || t(`${P}.flowCreateError`));
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={tr}>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ViewKanbanIcon color="primary" />
          {t(`${P}.title`)}
        </DialogTitle>

        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Stack spacing={2.5}>
            <TextField
              label={`${t(`${P}.flowTitle`)} *`}
              value={flowTitle}
              onChange={(e) => setFlowTitle(e.target.value)}
              fullWidth size="small" disabled={loading}
              inputProps={{ maxLength: 120 }}
            />

            <TextField
              label={t(`${P}.descriptionPlaceholder`)}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth size="small" multiline rows={2} disabled={loading}
            />

            {/* Flow Type (CONTINUOUS / PERIOD_BASED) */}
            <FormControl component="fieldset" disabled={loading}>
              <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                {t(`${P}.flowType`)}
              </FormLabel>
              <RadioGroup row value={flowType} onChange={(e) => setFlowType(e.target.value)}>
                <FormControlLabel value="CONTINUOUS" control={<Radio size="small" />} label={t(`${P}.flowTypeContinuous`)} />
                <FormControlLabel value="PERIOD_BASED" control={<Radio size="small" />} label={t(`${P}.flowTypePeriodBased`)} />
              </RadioGroup>
            </FormControl>

            {/* Conditional date inputs based on flow_type */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <DatePicker
                label={`${t(`${P}.startDate`)} *`}
                value={startDate}
                onChange={setStartDate}
                disabled={loading}
                format="dd.MM.yyyy"
                views={['year', 'month', 'day']}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
              />
              {flowType === 'PERIOD_BASED' && (
                <DatePicker
                  label={`${t(`${P}.endDate`)} *`}
                  value={endDate}
                  onChange={setEndDate}
                  disabled={loading}
                  format="dd.MM.yyyy"
                  views={['year', 'month', 'day']}
                  slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
                />
              )}
            </Box>

            {/* Report Types multi-select (empty = all) */}
            <FormControl size="small" fullWidth disabled={loading}>
              <InputLabel shrink>{t(`${P}.reportTypes`)}</InputLabel>
              <Select
                multiple
                value={reportTypeIds}
                onChange={(e) => setReportTypeIds(e.target.value)}
                input={<OutlinedInput notched label={t(`${P}.reportTypes`)} />}
                displayEmpty
                renderValue={(selected) => {
                  if (!selected || selected.length === 0) {
                    return (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {t(`${P}.reportTypesAll`)}
                      </Typography>
                    );
                  }
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((id) => {
                        const rt = (reportTypes || []).find((r) => String(r.id) === String(id));
                        return (
                          <Chip
                            onClick={() => {}}
                            key={id}
                            label={rt?.report_type_name || `#${id}`}
                            size="small"
                            sx={{ height: 20, fontSize: '0.72rem' }}
                          />
                        );
                      })}
                    </Box>
                  );
                }}
              >
                {(reportTypes || []).map((rt) => (
                  <MenuItem key={rt.id} value={String(rt.id)}>
                    {rt.report_type_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth disabled={loading}>
              <InputLabel>{t(`${P}.jobTypes`)}</InputLabel>
              <Select
                multiple value={jobTypes}
                onChange={(e) => setJobTypes(e.target.value)}
                input={<OutlinedInput label={t(`${P}.jobTypes`)} />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((val) => {
                      const opt = JOB_TYPE_OPTIONS.find((o) => o.value === val);
                      return <Chip onClick={() => {}} key={val} label={opt?.label || val} size="small" sx={{ height: 20, fontSize: '0.72rem' }} />;
                    })}
                  </Box>
                )}
              >
                {JOB_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl component="fieldset" disabled={loading}>
              <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.5 }}>
                {t(`${P}.sourceType`)}
              </FormLabel>
              <RadioGroup row value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
                <FormControlLabel value="JOB_LIST" control={<Radio size="small" />} label={t(`${P}.fromJobList`)} />
                <FormControlLabel value="REPORT_PLAN" control={<Radio size="small" />} label={t(`${P}.fromReportPlan`)} />
              </RadioGroup>
            </FormControl>

            {sourceType === 'JOB_LIST' && (
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
                  {t(`${P}.jobListFilter`, { defaultValue: 'İş Listesi Filtresi' })}
                </Typography>
                <FormControl size="small" fullWidth disabled={loading}>
                  <InputLabel shrink>{t(`${P}.jobStatuses`)}</InputLabel>
                  <Select
                    multiple
                    value={jobStatuses}
                    onChange={(e) => setJobStatuses(e.target.value)}
                    input={<OutlinedInput notched label={t(`${P}.jobStatuses`)} />}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected || selected.length === 0) {
                        return (
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {t(`${P}.jobStatusesAll`)}
                          </Typography>
                        );
                      }
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((val) => {
                            const opt = JOB_STATUS_OPTIONS.find((o) => o.value === val);
                            return (
                              <Chip
                                onClick={() => {}}
                                key={val}
                                label={opt?.label || val}
                                size="small"
                                sx={{ height: 20, fontSize: '0.72rem' }}
                              />
                            );
                          })}
                        </Box>
                      );
                    }}
                  >
                    {JOB_STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {sourceType === 'REPORT_PLAN' && (
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'grey.50' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary' }}>
                  {t(`${P}.reportPlanFilter`)}
                </Typography>
                <Stack spacing={2}>
                  <FormControl size="small" fullWidth disabled={loading}>
                    <InputLabel>{t(`${P}.filterTags`)}</InputLabel>
                    <Select
                      multiple
                      value={filterTags}
                      onChange={(e) => setFilterTags(e.target.value)}
                      input={<OutlinedInput label={t(`${P}.filterTags`)} />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((tag) => (
                            <Chip onClick={() => {}} key={tag} label={tag} size="small" sx={{ height: 20, fontSize: '0.72rem' }} />
                          ))}
                        </Box>
                      )}
                    >
                      {allTagOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Box>
            )}

            <Divider />

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {t(`${P}.columns`)} ({columns.length})
                </Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addColumn} disabled={loading} variant="outlined">
                  {t(`${P}.addColumn`)}
                </Button>
              </Box>

              <Stack spacing={1.5}>
                {columns.map((col, idx) => (
                  <Box
                    key={col._key}
                    sx={{
                      display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 1.5,
                      border: '1px solid', borderColor: 'divider',
                      borderLeft: `4px solid ${col.column_color}`,
                      borderRadius: 2, bgcolor: 'background.paper',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, pt: 0.5 }}>
                      <IconButton size="small" onClick={() => moveColumn(idx, -1)} disabled={idx === 0 || loading} sx={{ p: 0.25 }}>
                        <ArrowUpwardIcon sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                      <IconButton size="small" onClick={() => moveColumn(idx, 1)} disabled={idx === columns.length - 1 || loading} sx={{ p: 0.25 }}>
                        <ArrowDownwardIcon sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </Box>

                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <TextField
                        label={`${t(`${P}.columnTitle`)} *`}
                        value={col.column_title}
                        onChange={(e) => updateCol(col._key, 'column_title', e.target.value)}
                        size="small" fullWidth disabled={loading}
                        inputProps={{ maxLength: 80 }}
                      />
                      <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
                          {t(`${P}.columnColor`)}
                        </Typography>
                        <ColorPicker value={col.column_color} onChange={(c) => updateCol(col._key, 'column_color', c)} />
                      </Box>
                      <FormControl size="small" fullWidth disabled={loading}>
                        <InputLabel>{t(`${P}.columnAction`)}</InputLabel>
                        <Select
                          label={t(`${P}.columnAction`)}
                          value={col.column_action || ''}
                          onChange={(e) => updateColAction(col._key, e.target.value || null)}
                        >
                          <MenuItem value="">
                            <em>{t(`${P}.actionNone`)}</em>
                          </MenuItem>
                          <MenuItem value="SET_REPORT_COMPLETED">
                            {t(`${P}.actionSetCompleted`)}
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    <Tooltip title={t(`${P}.deleteColumn`)}>
                      <IconButton size="small" color="error" onClick={() => removeColumn(col._key)} disabled={loading || columns.length <= 1} sx={{ mt: 0.5 }}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            {t(`${P}.cancel`)}
          </Button>
          <Button
            variant="contained" onClick={handleSubmit}
            disabled={loading || !flowTitle.trim()}
            startIcon={loading ? <CircularProgress size={16} /> : <ViewKanbanIcon />}
          >
            {loading ? t(`${P}.saving`) : t(`${P}.save`)}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CreateFlowDialog;
